import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CreditCard, ExternalLink, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useHub } from "@/components/AppShell";
import { Loader, PageHeader } from "@/components/common";
import { GiftCard } from "@/components/billing/GiftCard";
import { FoundingBanner, IntervalToggle, PlanCards, fday, money2, usePlan } from "@/components/billing/Plans";
import { FormDialog } from "@/components/kids/kidsUi";
import { Switch } from "@/components/ui/switch";

const ORANGE = "var(--glow-family)";
const fdate = (iso) => (iso ? new Date(iso).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "");

export function statusText(p) {
  switch (p.status) {
    case "trial": return `Trial · ${p.days_left} day${p.days_left === 1 ? "" : "s"} left`;
    case "active": return `Active · renews ${fdate(p.ends_at)}`;
    case "canceling": return `Canceling · ends ${fdate(p.ends_at)}`;
    case "past_due": return "Past due";
    case "lapsed": return "Lapsed";
    case "comped": return `Comped until ${fdate(p.ends_at)}`;
    case "gifted": return `Gifted by ${(p.by || "").split(" ")[0]} until ${fdate(p.ends_at)}`;
    default: return "Demo";
  }
}
const CHIP = { trial: "var(--st-blue)", active: "var(--st-emerald)", canceling: "var(--st-amber)", past_due: "239,68,68", lapsed: "239,68,68", comped: "var(--st-violet)", gifted: "var(--st-pink)", demo: "var(--st-violet)" };

function Meters({ m }) {
  if (!m?.limited) return null;
  const reset = fday(m.resets_on).replace(/, \d{4}$/, "");
  return (
    <div className="space-y-2" data-testid="billing-meters">
      {m.meters.map((x) => (
        <div key={x.meter} data-testid={`billing-meter-${x.meter}`}>
          <div className="flex justify-between text-sm"><span className="text-q-text">{x.label[0].toUpperCase() + x.label.slice(1)}: {x.used} of {x.limit}</span><span className="text-q-faint">resets {reset}</span></div>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-q-line"><div className="h-full rounded-full" style={{ width: `${Math.min(100, (x.used * 100) / Math.max(1, x.limit))}%`, background: x.used >= x.limit ? "rgb(239,68,68)" : `rgb(${ORANGE})` }} /></div>
        </div>
      ))}
    </div>
  );
}

function NonOwner({ hub }) {
  const p = usePlan(hub.id);
  if (hub.my_role === "kid") return null;
  if (!p) return <Loader />;
  return (
    <div data-testid="billing-page">
      <PageHeader eyebrow="Family" title="Plan" rgb={ORANGE} testid="billing-title" />
      <section className="q-card max-w-xl" data-testid="billing-nonowner">
        <p className="font-display text-xl font-extrabold text-q-text" data-testid="billing-plan">{p.name}</p>
        <p className="mt-2 text-sm text-q-muted" data-testid="billing-ask-owner">Ask {p.owner_name} about changing plans.</p>
      </section>
    </div>
  );
}

function ChangeDialog({ d, pick, onClose, onDone, base }) {
  const [pv, setPv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const name = d.catalog.plans.find((p) => p.key === pick.plan)?.name || "Roommates";
  useEffect(() => { api.get(`${base}/preview`, { params: pick }).then(({ data }) => setPv(data)).catch((e) => setErr(errMsg(e))); }, [base, pick]);
  const go = async () => {
    setBusy(true); setErr("");
    try {
      const body = { ...pick, accept_founding_loss: true, origin_url: window.location.origin };
      if (d.can_change) { const { data } = await api.post(`${base}/change`, body); toast.success(data.message); onDone(data); }
      else { const { data } = await api.post(`${base}/checkout`, body); window.location.href = data.url; return; }
    } catch (e) { setErr(errMsg(e, "We couldn't change your plan. Nothing was charged. Try again.")); }
    setBusy(false);
  };
  const down = pv?.direction === "downgrade" && d.can_change;
  return (
    <FormDialog open onClose={onClose} title={`${d.can_change ? (down ? "Move to" : "Switch to") : "Subscribe to"} ${name}`} testid="plan-change-dialog"
      description={d.can_change ? (down ? `This happens on ${fdate(pv?.when)}, at the end of your paid period. You can undo it until then.` : "This happens now. Stripe charges the prorated difference today.") : "You'll finish on Stripe's secure checkout."}>
      {!pv && !err ? <Loader /> : (
        <div className="space-y-3">
          {pv?.founding_warning && <p className="rounded-xl border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-sm font-semibold text-amber-200" data-testid="plan-founding-warning">{pv.founding_warning}: Family Plus monthly at $9.99 is only for Founding families.</p>}
          {down && <ul className="list-disc space-y-1 pl-5 text-sm text-q-text" data-testid="plan-change-list">{pv.changes.map((c) => <li key={c}>{c}</li>)}</ul>}
          {err && <p className="text-sm font-semibold text-red-300" role="alert" data-testid="plan-change-error">{err}</p>}
          <button className="q-btn q-btn-primary min-h-[44px] w-full" disabled={busy || !pv} onClick={go} data-testid="plan-change-confirm">{busy && <Loader2 className="h-4 w-4 animate-spin" />} {d.can_change ? (down ? `Change to ${name} on ${fdate(pv?.when)}` : `Switch to ${name} now`) : "Continue to checkout"}</button>
        </div>
      )}
    </FormDialog>
  );
}

export default function Billing() {
  const { hub, reloadHub } = useHub();
  const [q, setQ] = useSearchParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");
  const [iv, setIv] = useState("month");
  const [pick, setPick] = useState(null);
  const owner = hub.my_role === "owner" && hub.view_mode === "member";
  const base = `/organizations/${hub.id}/billing`;
  const load = useCallback(() => api.get(base).then(({ data }) => { setD(data); if (data.interval) setIv(data.interval); }).catch((e) => setErr(errMsg(e))), [base]);
  useEffect(() => {
    if (!owner) return;
    const sid = q.get("session_id");
    if (q.get("checkout") === "success" && sid) {
      setNote("Confirming with Stripe…");
      let n = 0;
      const poll = async () => {
        n += 1;
        try {
          const { data } = await api.get(`${base}/session/${sid}`);
          if (data.subscribed) { setD(data); setNote(`You're on ${data.plan.name} now. Thank you!`); reloadHub(); setQ({}, { replace: true }); return; }
        } catch (e) { setNote(errMsg(e)); return; }
        if (n < 10) setTimeout(poll, 2000); else setNote("Payment is still processing. Check back in a minute.");
      };
      poll();
    } else if (q.get("checkout") === "cancel") { setNote("Checkout canceled. No changes were made."); setQ({}, { replace: true }); load(); }
    else if (q.get("portal")) { api.post(`${base}/refresh`).then(({ data }) => { setD(data); reloadHub(); }); setQ({}, { replace: true }); }
    else load();
  }, [hub.tier?.key]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!owner) return <NonOwner hub={hub} />;
  if (err) return <div className="q-card" data-testid="billing-forbidden">{err}</div>;
  if (!d) return <Loader />;
  const p = d.status;
  const portal = async () => {
    setBusy("portal");
    try { const { data } = await api.post(`${base}/portal`, { origin_url: window.location.origin }); window.location.href = data.url; } catch (e) { toast.error(errMsg(e)); setBusy(""); }
  };
  const keep = async () => { try { setD((await api.delete(`${base}/change`)).data); toast.success(`You're staying on ${d.plan.name}`); } catch (e) { toast.error(errMsg(e)); } };
  const early = async (on) => { try { await api.put(`${base}/early-access`, { on }); setD({ ...d, early_access: on }); } catch (e) { toast.error(errMsg(e)); } };
  const rm = d.roommates;
  const price = rm ? `${rm.count} roommate${rm.count === 1 ? "" : "s"} · ${money2(rm.total_cents)}/mo` : `${money2(d.price_cents)}/${d.interval === "year" ? "yr" : "mo"}`;
  return (
    <div data-testid="billing-page">
      <PageHeader eyebrow="Family" title="Billing" rgb={ORANGE} testid="billing-title" />
      {note && <p className="mb-4 rounded-xl border border-q-line bg-q-raised px-4 py-3 text-sm font-semibold text-q-text" data-testid="billing-note">{note}</p>}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <section className="q-card space-y-5" data-testid="billing-card">
          <div className="flex items-start gap-4">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl" style={{ background: `rgba(${ORANGE},0.15)` }}><CreditCard className="h-6 w-6" style={{ color: `rgb(${ORANGE})` }} /></span>
            <div className="flex-1">
              <p className="font-display text-xl font-extrabold text-q-text" data-testid="billing-plan">{d.plan.name} · <span data-testid="billing-price">{price}</span></p>
              {d.trial_label && <p className="text-sm font-semibold text-q-text" data-testid="billing-trial-label">{d.trial_label}</p>}
              <span className="mt-2 inline-flex rounded-full px-3 py-1 text-xs font-black uppercase tracking-[0.12em]" style={{ background: `rgba(${CHIP[p.status]},0.16)`, color: `rgb(${CHIP[p.status]})` }} data-testid="billing-status-chip">{statusText(p)}</span>
            </div>
          </div>
          {d.founding && <p className="rounded-xl border border-[rgba(var(--glow-family),0.4)] bg-[rgba(var(--glow-family),0.08)] px-3 py-2 text-sm font-semibold text-q-text" data-testid="billing-founding">
            <Sparkles className="mr-1 inline h-4 w-4 text-[rgb(var(--glow-family))]" />{d.founding.state === "claimed" && d.founding.until ? `Founding family · $9.99/mo until ${fdate(d.founding.until)}, then ${money2(d.founding.then_cents)}/mo` : d.founding.banner}</p>}
          {d.pending && <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-400/40 bg-amber-400/10 px-3 py-2" data-testid="billing-pending">
            <span className="text-sm font-semibold text-amber-100">Changes to {d.pending.name} on {fdate(d.pending.at)}</span>
            <button className="q-btn q-btn-secondary min-h-[40px] text-xs" onClick={keep} data-testid="billing-keep-plan">Keep {d.plan.name}</button></div>}
          <div data-testid="billing-storage"><p className="text-sm text-q-muted">Storage: {d.storage.label}</p>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-q-line"><div className="h-full rounded-full bg-[rgb(var(--glow-family))]" style={{ width: `${Math.min(100, d.storage.percent)}%` }} /></div></div>
          <Meters m={d.meters} />
          {p.status === "past_due" && <p className="text-sm font-semibold" style={{ color: "rgb(var(--q-danger))" }} data-testid="billing-past-due-note">We couldn't charge your card. Update it to keep your hub.</p>}
          {p.status === "lapsed" && <p className="text-sm text-q-muted" data-testid="billing-lapsed-note">Your hub is read-only. Nothing was deleted. Pick a plan to unlock it.</p>}
          {p.status === "demo" && <p className="text-sm text-q-muted" data-testid="billing-demo-note">Demo hubs aren't billed.</p>}
          {d.features.priority && <label className="flex items-center justify-between gap-3 rounded-xl border border-q-line p-3" data-testid="billing-early-access">
            <span><span className="block font-semibold text-q-text">Early access</span><span className="text-sm text-q-muted">Try new features before everyone else</span></span>
            <Switch checked={d.early_access} onCheckedChange={early} disabled={d.is_demo} data-testid="billing-early-access-switch" /></label>}
          <div className="flex flex-wrap gap-2">
            {rm && d.can_subscribe && <button className="q-btn q-btn-primary" onClick={() => setPick({ plan: "roommates", interval: "month" })} data-testid="billing-subscribe-btn">Subscribe · {money2(rm.total_cents)}/mo</button>}
            {d.can_manage && <button className="q-btn q-btn-secondary" disabled={!!busy} onClick={portal} data-testid="billing-manage-btn">{busy === "portal" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />} {p.status === "past_due" ? "Update card" : "Card and invoices"}</button>}
            <button className="q-btn q-btn-ghost" onClick={() => nav(`/h/${hub.id}/home`)} data-testid="billing-home-btn">Back to Home</button>
          </div>
          <p className="text-xs text-q-faint">Payments are handled by Stripe. One plan per hub, paid by the owner.</p>
        </section>
        {!d.is_demo && p.status !== "demo" && <GiftCard hubId={hub.id} onGifted={() => { load(); reloadHub(); }} />}
      </div>
      {!rm && (
        <section className="mt-8 space-y-4" data-testid="billing-plans">
          <div className="flex flex-wrap items-center justify-between gap-3"><h2 className="font-display text-lg font-bold text-q-text">Plans</h2><IntervalToggle value={iv} onChange={setIv} /></div>
          {(p.status === "trial" || p.status === "lapsed") && <FoundingBanner plans={d.catalog} testid="billing-founding-banner" />}
          <PlanCards plans={d.catalog} interval={iv} current={d.can_change && d.interval === iv ? d.plan.key : null} busy={d.is_demo || !(d.can_change || d.can_subscribe)}
            onPick={(plan) => setPick({ plan, interval: iv })} cta={d.can_change ? "Switch to" : "Choose"} />
          {d.is_demo && <p className="text-sm text-q-muted">Demo hubs aren't billed. Admins can switch the demo plan from the demo toolbar.</p>}
        </section>
      )}
      {pick && <ChangeDialog d={d} pick={pick} base={base} onClose={() => setPick(null)} onDone={(data) => { setD(data); setPick(null); reloadHub(); }} />}
    </div>
  );
}
